// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
import { CheckCircle, XCircle } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import { type CommentWithOptionalTags } from "./reviewCommentTags";

type Decider = { userId: string; name?: string } | undefined;

/** Who made a past decision, as the card says it. */
export type JudgedBy =
  | { kind: "you" }
  | { kind: "name"; name: string }
  | { kind: "role"; role: string }
  | { kind: "unknown" };

/**
 * The enterprise roles a judgment records (`EnterpriseProfileType`), each with
 * a label in `assetViewer.reviewerRoles`.
 *
 * why a closed list: the role is a storage slug, and the first live receipt
 * printed "Rejected before by a meaningful-gigs reviewer". A role outside this
 * list is never shown; the card says only that it was decided before.
 */
const LABELLED_ROLES = new Set([
  "meaningful-gigs",
  "admin",
  "admin-with-ai",
  "ops",
  "expert",
  "marketing",
  "creative",
  "requester",
  "agency",
  "freelancer",
  "employee",
  "guest",
]);

/**
 * The viewer's own decision first, then the decider's name, then a role label.
 * christo reads `decidedBy` from the judgment ledger when it serves the review
 * (`reviewJudgmentDeciders.ts`), so a receipt stored before it existed names
 * its decider too.
 */
export function judgedBy(
  decidedBy: Decider,
  role: string | undefined,
  currentUserId: string | undefined,
): JudgedBy {
  if (decidedBy?.userId && currentUserId && decidedBy.userId === currentUserId)
    return { kind: "you" };
  const name = decidedBy?.name?.trim();
  if (name) return { kind: "name", name };
  const slug = role?.trim().toLowerCase();
  if (slug && LABELLED_ROLES.has(slug)) return { kind: "role", role: slug };
  return { kind: "unknown" };
}

function receiptLabel(
  t: (key: string, options?: Record<string, unknown>) => string,
  verdict: "rejected" | "confirmed",
  who: JudgedBy,
): string {
  const stem =
    verdict === "rejected"
      ? "assetViewer.previouslyRejected"
      : "assetViewer.previouslyConfirmed";
  switch (who.kind) {
    case "you":
      return t(stem);
    case "name":
      return t(`${stem}ByName`, { name: who.name });
    case "role":
      return t(`${stem}By`, {
        role: t(`assetViewer.reviewerRoles.${who.role}`),
      });
    default:
      return t(`${stem}Unknown`);
  }
}

/**
 * The receipt a demoted finding carries, as a subheading under its title.
 *
 * why: a small gray chip among the tag badges read as metadata; the first
 * live check had to open the card to find it at all. Directly under the title,
 * with the reject icon, it is the second thing she reads about the finding --
 * on the collapsed card and the open one alike, which are mutually exclusive
 * per comment, so the testid stays unique on the page.
 */
export function PreviouslyRejectedSubheading({
  comment,
  commentId,
  currentUserId,
}: {
  comment: CommentWithOptionalTags;
  commentId: string;
  currentUserId?: string;
}) {
  const { t } = useTranslation("ui");
  const receipt = comment.previouslyRejected;
  if (!receipt) return null;
  return (
    <Text
      as="p"
      size="2"
      weight="medium"
      className="mt-1 flex items-center gap-1.5 text-puntt-neutral-gray-11"
      data-testid={`ask-v2-review-comment-previously-rejected-${commentId}`}
    >
      <XCircle size={16} weight="bold" aria-hidden="true" />
      {receiptLabel(
        t,
        "rejected",
        judgedBy(receipt.decidedBy, receipt.role, currentUserId),
      )}
    </Text>
  );
}

/**
 * A finding a reviewer already confirmed as an issue: green, under the title,
 * so it reads as agreed work rather than something new.
 */
export function PreviouslyConfirmedSubheading({
  comment,
  commentId,
  currentUserId,
}: {
  comment: CommentWithOptionalTags;
  commentId: string;
  currentUserId?: string;
}) {
  const { t } = useTranslation("ui");
  const receipt = comment.previouslyConfirmed;
  if (!receipt || comment.previouslyRejected) return null;
  return (
    <Text
      as="p"
      size="2"
      weight="medium"
      color="green"
      // why: Radix's own green, as the Confirm button uses -- the tailwind
      // `text-puntt-green-11` class rendered gray in the live panel.
      className="mt-1 flex items-center gap-1.5"
      data-testid={`ask-v2-review-comment-previously-confirmed-${commentId}`}
    >
      <CheckCircle size={16} weight="bold" aria-hidden="true" />
      {receiptLabel(
        t,
        "confirmed",
        judgedBy(receipt.decidedBy, receipt.role, currentUserId),
      )}
    </Text>
  );
}
